<script setup lang="ts">
    import { CardMenu } from '@/components/ui/card';
    import { createVoucherEarlierReleasesColumns } from '@/tables/columns/voucher-earlier-releases';
    import { computed } from 'vue';
    import { DatePicker } from '@/components/ui/form';
    import { Head } from '@inertiajs/vue3';
    import { ref } from 'vue';
    import { today, getLocalTimeZone } from '@internationalized/date';
    import { usePage } from '@inertiajs/vue3';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableSearchButton from '@/components/datatable/DataTableSearchButton.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem, NavItem } from '@/types';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const startDate = ref<string>(today(getLocalTimeZone()).subtract({ years: 1 }).toString());
    const endDate = ref<string>(today(getLocalTimeZone()).toString());

    const { data: economicDimensions, isPending: isPendingDimensions } = useQuery({
        queryKey: ['economic-dimensions', selectedCompanyId],
        queryFn: async () => {
            if (!selectedCompanyId.value) return [];
            const { data } = await axios.get(route('v2.companies.get', selectedCompanyId.value));
            return data.economic_dimensions || [];
        },
        enabled: computed(() => !!selectedCompanyId.value),
        placeholderData: [],
        retry: 1,
        refetchOnMount: true,
    });

    const columns = computed(() => createVoucherEarlierReleasesColumns(economicDimensions.value || []));

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'vouchers.page_header',
        },
    ];

    const navItems: NavItem[] = [
        {
            title: 'vouchers.current_vouchers.header',
            href: route('v2.vouchers.index'),
            permissionLevel: Permission.VOUCHERS_VIEW,
        },
        {
            title: 'vouchers.earlier_releases.header',
            href: route('v2.vouchers.earlier-releases.index'),
            permissionLevel: Permission.VOUCHERS_EARLIER_RELEASES_VIEW,
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.VOUCHERS_EARLIER_RELEASES, selectedCompanyId],
        queryFn: getVouchers,
    });

    async function getVouchers() {
        const { data } = await axios.post(
            route('v2.vouchers.earlier-releases.load-table', selectedCompanyId.value),
            {
                from: startDate.value,
                to: endDate.value,
            },
        );
        return data.data;
    }
</script>

<template>
    <Head :title="$t('vouchers.page_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.VOUCHERS_EARLIER_RELEASES"
                :columns="columns"
                :data="data"
                :loading="isPending || isPendingDimensions"
                :default-sorting="[
                    {
                        id: 'Date',
                        desc: false,
                    },
                ]"
            >
                <template #title>
                    <CardMenu :items="navItems" />
                </template>
                <template #actions>
                    <DataTableToolbarActions name="vouchers-earlier-releases">
                        <span class="text-[10px] uppercase">
                            {{ $t('activities.date_selection') }}
                        </span>
                        <DatePicker v-model="startDate" />
                        -
                        <DatePicker v-model="endDate" />
                        <DataTableSearchButton
                            @refetch="refetch"
                            :is-fetching="isFetching"
                        />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
